import { createElement } from "react";
import { Plus, Trash2 } from "lucide-react";

const REG = {};
const Nested = (props) => createElement(REG.FieldEditor, props);
import { Switch } from "../ui/switch";
import RichTextEditor from "./RichTextEditor";
import MediaPicker from "./MediaPicker";
import { humanize } from "../../lib/format";

const blankLike = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, typeof v === "boolean" ? false : typeof v === "number" ? 0 : ""]));

function ListEditor({ id, value, onChange }) {
  const template = value[0] ? blankLike(value[0]) : { title: "", text: "" };
  return (
    <div className="space-y-3">
      {value.map((item, i) => (
        <div key={i} className="rounded border border-slate-200 bg-slate-50 p-3">
          <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-600">Item {i + 1}
            <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} aria-label={`Remove item ${i + 1}`} className="text-red-700" data-testid={`${id}-remove-${i}`}><Trash2 size={14} /></button>
          </div>
          <Nested idPrefix={`${id}-${i}`} value={item} onChange={(v) => onChange(value.map((x, k) => (k === i ? v : x)))} />
        </div>
      ))}
      <button type="button" className="inline-flex items-center gap-1 text-sm font-semibold text-burgundy" onClick={() => onChange([...value, template])} data-testid={`${id}-add`}>
        <Plus size={14} aria-hidden="true" /> Add item</button>
    </div>
  );
}

function FieldRow({ id, name, value, onChange }) {
  const label = humanize(name);
  let control;
  if (typeof value === "boolean") control = <Switch id={id} checked={value} onCheckedChange={onChange} data-testid={id} />;
  else if (typeof value === "number") control = <input id={id} type="number" className="field-input text-sm" value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={id} />;
  else if (Array.isArray(value)) control = <ListEditor id={id} value={value} onChange={onChange} />;
  else if (value && typeof value === "object") control = <div className="rounded border border-slate-200 p-3"><Nested idPrefix={id} value={value} onChange={onChange} /></div>;
  else if (name === "body" && (value || "").includes("<")) control = <RichTextEditor id={id} label={label} value={value} onChange={onChange} />;
  else if (name.endsWith("image_url") || name === "logo_url") control = <MediaPicker id={id} value={value} onChange={onChange} />;
  else if ((value || "").length > 90 || name === "body" || name === "description") control = <textarea id={id} rows={3} className="field-input text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  else control = <input id={id} className="field-input text-sm" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  const hint = name === "video_url" ? "YouTube, Vimeo or uploaded video URL. Leave empty to hide the play button." : name === "unverified" ? "Marks figures as unverified targets."
    : name === "billing_descriptor" ? "Confirmed descriptor (matches the bank/gateway). Shown at card checkout, on confirmations and in receipts; change only if the bank changes it."
    : name === "contact_phone" ? "Hidden on the site while empty." : null;
  return (
    <div>
      <label htmlFor={id} className="field-label text-xs uppercase tracking-wide">{label}</label>
      {control}
      {hint && <p className="mt-1 text-xs text-[#4A5568]">{hint}</p>}
    </div>
  );
}

export default function FieldEditor({ value, onChange, idPrefix = "fe" }) {
  return (
    <div className="space-y-4">
      {Object.entries(value || {}).map(([k, v]) => (
        <FieldRow key={k} id={`${idPrefix}-${k}`} name={k} value={v} onChange={(nv) => onChange({ ...value, [k]: nv })} />
      ))}
    </div>
  );
}

REG.FieldEditor = FieldEditor;
